/**
 * Service for managing advisor access with caching
 * Similar to investment access patterns but for advisor relationships
 */

import BackendService from 'BackendService';
import { AdvisorAccess, AdvisorRole } from '../models/Advisor';

class AdvisorAccessService {
  private cachedAccess: AdvisorAccess[] | null = null;
  private cacheTimestamp: number | null = null;
  private readonly CACHE_DURATION = 5 * 60 * 1000; // 5 minutes
  private isAdmin: boolean = false;

  constructor(isAdmin: boolean = false) {
    this.isAdmin = isAdmin;
  }

  /**
   * Get advisor access with caching
   */
  async getAdvisorAccess(): Promise<AdvisorAccess[]> {
    const now = Date.now();

    // Return cached data if still valid
    if (
      this.cachedAccess &&
      this.cacheTimestamp &&
      now - this.cacheTimestamp < this.CACHE_DURATION
    ) {
      return this.cachedAccess;
    }

    try {
      const backendService = new BackendService();
      const access = await backendService.getAdvisorAccess();

      this.cachedAccess = access;
      this.cacheTimestamp = now;

      return access;
    } catch (error) {
      console.error('Failed to fetch advisor access:', error);
      // Return cached data if available, even if expired
      return this.cachedAccess || [];
    }
  }

  /**
   * Check if user has advisor access to any entity
   */
  async hasAdvisorAccess(entityId?: string): Promise<boolean> {
    const access = await this.getAdvisorAccess();

    if (!entityId) {
      return access.length > 0;
    }

    return access.some((advisorAccess) =>
      advisorAccess.entities.some((entity) => entity.id === entityId),
    );
  }

  /**
   * Get advisor role for a specific entity
   */
  async getAdvisorRole(entityId?: string): Promise<AdvisorRole | null> {
    const access = await this.getAdvisorAccess();

    if (!entityId) {
      // Return the highest role if no specific entity
      const roles = access.map((a) => a.role);
      if (roles.includes('DECISION_MAKER')) {
        return 'DECISION_MAKER';
      }
      return roles.length > 0 ? roles[0] : null;
    }

    const advisorAccess = access.find((a) => a.entities.some((entity) => entity.id === entityId));

    return advisorAccess?.role || null;
  }

  /**
   * Get all accessible entities
   */
  async getAccessibleEntities(): Promise<Array<{ id: string; name: string }>> {
    const access = await this.getAdvisorAccess();
    const entities: Array<{ id: string; name: string }> = [];

    access.forEach((advisorAccess) => {
      advisorAccess.entities.forEach((entity) => {
        if (!entities.find((e) => e.id === entity.id)) {
          entities.push(entity);
        }
      });
    });

    return entities;
  }

  /**
   * Clear cache to force refresh
   */
  clearCache(): void {
    this.cachedAccess = null;
    this.cacheTimestamp = null;
  }

  /**
   * Force refresh of advisor access
   */
  async refreshAdvisorAccess(): Promise<AdvisorAccess[]> {
    this.clearCache();
    return this.getAdvisorAccess();
  }

  /**
   * Get cached access without making API call
   */
  getCachedAccess(): AdvisorAccess[] | null {
    return this.cachedAccess;
  }

  /**
   * Check if cache is valid
   */
  isCacheValid(): boolean {
    if (!this.cachedAccess || !this.cacheTimestamp) {
      return false;
    }

    const now = Date.now();
    return now - this.cacheTimestamp < this.CACHE_DURATION;
  }
}

// Create singleton instances
export const advisorAccessService = new AdvisorAccessService(false);
export const adminAdvisorAccessService = new AdvisorAccessService(true);

export default AdvisorAccessService;
